/* SandboxLinkGenerator — reusable component to mint + manage
 * shareable demo links.
 *
 * iter98.93 — Shareable sandbox demo links are an Invisique-wide
 * lever: admins use them in marketing, sales reps use them for cold
 * outbound, doctors use them as "Refer a colleague" affordances. The
 * same component renders in all three portals — the backend endpoint
 * decides which links to show (admin sees all; sales/doctor see only
 * theirs) and which roles can mint.
 *
 * Affordances:
 *   - "+ Generate new link" → opens an inline composer (optional
 *     internal note for the creator to remember which prospect this
 *     link is for) → POST /share-demo/links → adds to the table.
 *   - Each row: short token, internal note, claim count, expiry
 *     countdown, copy-URL button, revoke button.
 *   - Public URL is built from window.location.origin so it works
 *     identically on preview + production.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import {
    Plus, Copy, X, ShareNetwork, Users as UsersIcon, Clock, Sparkle, TrendUp, CheckCircle,
    WhatsappLogo, EnvelopeSimple,
} from "@phosphor-icons/react";

function fmtTimeLeft(expiresIso) {
    if (!expiresIso) return "";
    const expires = new Date(expiresIso).getTime();
    const now = Date.now();
    const ms = expires - now;
    if (ms <= 0) return "Expired";
    const days = Math.floor(ms / 86_400_000);
    const hours = Math.floor((ms % 86_400_000) / 3_600_000);
    if (days >= 1) return `${days}d ${hours}h left`;
    const mins = Math.floor(ms / 60_000);
    return `${mins} min left`;
}

function publicUrl(token) {
    if (typeof window === "undefined") return `/sandbox/${token}`;
    return `${window.location.origin}/sandbox/${token}`;
}


/* iter100.177 — Pre-drafted share copy for the "Refer a colleague" flow.
   Doctors kept copying the raw URL then hand-typing pitches into WhatsApp,
   so we ship the pitch for them. Kept warm + peer-to-peer (not marketing
   speak); mentions the sandbox affordance + expiry so the recipient
   doesn't sit on it for a week. `senderName` is optional and shows up in
   the sign-off when we have it (e.g. "— Dr. Wilson"). */
function buildShareCopy(url, senderName) {
    const signoff = senderName ? `\n\n— ${senderName}` : "";
    const body =
`Hey — thought you'd want to see Invisique™, the aligner platform I've been using.

They set up a full sandbox portal for me to share (no signup, no card):
${url}

You can click through the doctor workflow end-to-end — submitting a case, planner chat, activation, everything. Link expires in 7 days.${signoff}`;
    return {
        subject: "A quick look at Invisique™",
        whatsapp: body,
        email: body,
    };
}

function openShareWhatsapp(url, senderName) {
    const copy = buildShareCopy(url, senderName);
    const href = `https://wa.me/?text=${encodeURIComponent(copy.whatsapp)}`;
    if (typeof window !== "undefined") window.open(href, "_blank", "noopener,noreferrer");
}

function openShareEmail(url, senderName) {
    const copy = buildShareCopy(url, senderName);
    const href = `mailto:?subject=${encodeURIComponent(copy.subject)}&body=${encodeURIComponent(copy.email)}`;
    if (typeof window !== "undefined") window.location.href = href;
}


export default function SandboxLinkGenerator({ title = null, description = null }) {
    const [links, setLinks] = useState([]);

    /* iter100.157ai — Conversion funnel rollup for the KPI strip.
       "Real conversions" = visitors who claimed the link AND then
       promoted their sandbox account to a real Invisique portal via
       the /doctor-signup wizard (backend stamps `claimed_real_at`). */
    const stats = useMemo(() => {
        const active = links.filter((l) => !l.revoked_at);
        const totalClaims = links.reduce((s, l) => s + (l.claim_count || 0), 0);
        const totalConv = links.reduce((s, l) => s + (l.conversion_count || 0), 0);
        const rate = totalClaims > 0 ? Math.round((totalConv / totalClaims) * 100) : 0;
        return {
            total: links.length,
            active: active.length,
            claims: totalClaims,
            conversions: totalConv,
            rate,
        };
    }, [links]);
    const [loading, setLoading] = useState(true);
    const [composing, setComposing] = useState(false);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/share-demo/links");
            setLinks(data || []);
        } catch (err) {
            // 403 means current role can't mint — render an empty
            // state with a helpful message rather than a toast.
            if (err?.response?.status === 403) {
                setLinks(null);  // null = forbidden marker
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post("/share-demo/links", { note: note.trim() });
            toast.success("Demo link created — copy & share");
            setNote("");
            setComposing(false);
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create link");
        } finally {
            setSubmitting(false);
        }
    };

    const copy = (token) => {
        const url = publicUrl(token);
        if (typeof navigator !== "undefined" && navigator.clipboard) {
            navigator.clipboard.writeText(url).then(
                () => toast.success("Copied to clipboard"),
                () => toast.error("Copy failed — long-press the link below"),
            );
        }
    };

    const revoke = async (token) => {
        if (!await confirmDialog({ title: "Revoke this demo link? Anyone who already claimed it keeps their sandbox session, but no new claims will work.", confirmText: "Confirm", tone: "danger" })) return;
        try {
            await api.delete(`/share-demo/links/${token}`);
            toast.success("Link revoked");
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to revoke");
        }
    };

    if (links === null) {
        return (
            <div className="rounded-2xl border border-border bg-card p-5">
                <div className="text-[12px] text-muted-foreground">
                    Your role can&apos;t generate sandbox demo links. Ask an admin or sales lead to share one with you.
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-3xl border border-border bg-card overflow-hidden" data-testid="sandbox-link-generator">
            {/* Header */}
            <div className="px-5 py-4 border-b border-border bg-secondary/20 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl grid place-items-center bg-primary/10 text-primary shrink-0">
                        <ShareNetwork size={20} weight="duotone" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="font-display text-base font-bold text-foreground leading-tight">
                            {title || "Share a demo link"}
                        </h2>
                        <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed max-w-2xl">
                            {description || "Generate a magic URL that drops the recipient into a fully-functional Invisique™ portal — no signup needed. Links expire in 7 days, capture name + email + phone on claim."}
                        </p>
                    </div>
                </div>
                {!composing && (
                    <Button
                        onClick={() => setComposing(true)}
                        className="bg-primary text-primary-foreground"
                        data-testid="sandbox-link-new-btn"
                    >
                        <Plus size={13} className="mr-1.5" weight="bold" />
                        Generate new link
                    </Button>
                )}
            </div>

            {/* iter100.157ai — Conversion KPI strip.
                Renders only when we've actually got data, so brand-new
                accounts aren't distracted by a wall of zeros. */}
            {links.length > 0 && (
                <div
                    className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border/70 border-b border-border"
                    data-testid="sandbox-link-kpi-strip"
                >
                    <KpiCell label="Links" value={stats.total} sub={`${stats.active} active`} />
                    <KpiCell
                        label="Claims"
                        value={stats.claims}
                        sub={stats.claims > 0 ? "sandbox visitors" : "none yet"}
                        icon={<UsersIcon size={13} weight="duotone" className="text-primary" />}
                    />
                    <KpiCell
                        label="Real conversions"
                        value={stats.conversions}
                        sub={stats.conversions > 0 ? "claimed → real portal" : "none yet"}
                        icon={<CheckCircle size={13} weight="fill" className="text-emerald-600" />}
                        highlight={stats.conversions > 0}
                    />
                    <KpiCell
                        label="Conversion rate"
                        value={`${stats.rate}%`}
                        sub={stats.claims ? `${stats.conversions} of ${stats.claims}` : "—"}
                        icon={<TrendUp size={13} weight="bold" className="text-violet-600" />}
                    />
                </div>
            )}

            {/* Composer */}
            {composing && (
                <div
                    className="px-5 py-4 border-b border-border bg-primary/[0.04]"
                    data-testid="sandbox-link-composer"
                >
                    <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/80 block mb-1.5">
                        Internal note (optional)
                    </label>
                    <Input
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. Cold outreach — Dr. Mehta · Vasant Vihar Dental"
                        className="bg-white"
                        maxLength={200}
                        data-testid="sandbox-link-note-input"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                        Only you (and admins) see this — helps you remember which prospect each link is for.
                    </p>
                    <div className="flex items-center justify-end gap-2 mt-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setComposing(false); setNote(""); }}
                            disabled={submitting}
                        >
                            <X size={12} className="mr-1" /> Cancel
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={submitting}
                            size="sm"
                            className="bg-primary text-primary-foreground"
                            data-testid="sandbox-link-create-btn"
                        >
                            <Sparkle size={12} className="mr-1.5" weight="fill" />
                            {submitting ? "Creating…" : "Create demo link"}
                        </Button>
                    </div>
                </div>
            )}

            {/* Links list */}
            <div className="divide-y divide-border">
                {loading && (
                    <div className="px-5 py-8 text-center text-[12px] text-muted-foreground">Loading…</div>
                )}
                {!loading && links.length === 0 && (
                    <div className="px-5 py-8 text-center text-[12.5px] text-muted-foreground">
                        No demo links yet. Click <span className="font-semibold text-foreground">Generate new link</span> to create your first shareable URL.
                    </div>
                )}
                {!loading && links.map((link) => (
                    <SandboxLinkRow
                        key={link.token}
                        link={link}
                        onCopy={() => copy(link.token)}
                        onRevoke={() => revoke(link.token)}
                    />
                ))}
            </div>
        </div>
    );
}


function SandboxLinkRow({ link, onCopy, onRevoke }) {
    const revoked = !!link.revoked_at;
    const expired = !revoked && fmtTimeLeft(link.expires_at) === "Expired";
    const inactive = revoked || expired;
    const url = publicUrl(link.token);
    /* iter100.177 — Sender name goes into the pre-drafted pitch. Prefer the
       backend-supplied `created_by_name` (already stamped for admin/sales/doc
       views); fall back to blank when we don't have one — the copy still
       reads fine without a sign-off. */
    const senderName = link.created_by_name || "";
    return (
        <div
            className={`px-5 py-3.5 ${inactive ? "opacity-60 bg-secondary/10" : "hover:bg-secondary/10"} transition-colors`}
            data-testid={`sandbox-link-row-${link.token}`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <code className="font-mono text-[12.5px] font-semibold text-foreground">
                            …/sandbox/{link.token}
                        </code>
                        {revoked && (
                            <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 ring-1 ring-rose-200">
                                Revoked
                            </span>
                        )}
                        {expired && (
                            <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                                Expired
                            </span>
                        )}
                        {!inactive && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                                <Clock size={9} weight="fill" /> {fmtTimeLeft(link.expires_at)}
                            </span>
                        )}
                    </div>
                    {link.note && (
                        <p className="text-[12px] text-muted-foreground mt-1 italic">&ldquo;{link.note}&rdquo;</p>
                    )}
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground/90">
                        <span className="inline-flex items-center gap-1">
                            <UsersIcon size={11} weight="duotone" />
                            <span className="font-semibold text-foreground/80">{link.claim_count || 0}</span> claim{link.claim_count === 1 ? "" : "s"}
                        </span>
                        {/* iter100.157ai — Show real-conversion chip only
                            when at least one visitor from this link went
                            all the way to a real portal. Keeps zero-state
                            rows visually quiet. */}
                        {link.conversion_count > 0 && (
                            <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200"
                                title={link.first_conversion_at ? `First conversion ${new Date(link.first_conversion_at).toLocaleDateString()}` : "Real conversions"}
                                data-testid={`sandbox-link-conversion-${link.token}`}
                            >
                                <CheckCircle size={9} weight="fill" />
                                <span className="text-[9px] font-bold uppercase tracking-[0.14em]">
                                    {link.conversion_count} real
                                </span>
                            </span>
                        )}
                        {link.created_by_name && (
                            <span>by <span className="font-medium text-foreground/80">{link.created_by_name}</span></span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                    {/* iter100.177 — Pre-drafted WhatsApp + Email share.
                        Doctors were copy-URL → paste-into-WhatsApp →
                        hand-write the pitch every time; now we ship the
                        pitch too. Deep-links to WhatsApp Web / native
                        mail composer with the sandbox URL pre-filled. */}
                    {!inactive && (
                        <>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openShareWhatsapp(url, senderName)}
                                className="h-7 px-2.5 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                                data-testid={`sandbox-link-whatsapp-${link.token}`}
                                title="Open WhatsApp with a pre-drafted invite"
                            >
                                <WhatsappLogo size={12} weight="fill" className="mr-1" />
                                WhatsApp
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openShareEmail(url, senderName)}
                                className="h-7 px-2.5 text-[11px]"
                                data-testid={`sandbox-link-email-${link.token}`}
                                title="Open your mail client with a pre-drafted invite"
                            >
                                <EnvelopeSimple size={12} weight="bold" className="mr-1" />
                                Email
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={onCopy}
                                className="h-7 px-2.5 text-[11px]"
                                data-testid={`sandbox-link-copy-${link.token}`}
                            >
                                <Copy size={11} className="mr-1" />
                                Copy URL
                            </Button>
                        </>
                    )}
                    {!revoked && (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onRevoke}
                            className="h-7 px-2 text-[11px] text-rose-700 hover:bg-rose-50"
                            data-testid={`sandbox-link-revoke-${link.token}`}
                        >
                            <X size={11} className="mr-0.5" />
                            Revoke
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}



/* KpiCell — compact metric tile for the conversion-funnel strip. */
function KpiCell({ label, value, sub, icon, highlight = false }) {
    return (
        <div className={`px-4 py-3 ${highlight ? "bg-emerald-50/70" : "bg-card"}`}>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1.5">
                {icon}
                {label}
            </div>
            <div className={`text-xl font-bold tabular-nums mt-0.5 leading-tight ${highlight ? "text-emerald-700" : "text-foreground"}`}>
                {value}
            </div>
            {sub && (
                <div className="text-[10.5px] text-muted-foreground mt-0.5">{sub}</div>
            )}
        </div>
    );
}
